import Logo, { LogoColorVariant } from '@/components/svgs/Logo'
import { useAuthStore } from '@/store'
import { useEffect } from 'react'
import { StyleSheet, View, useWindowDimensions } from 'react-native'
import Animated, {
	Easing,
	interpolate,
	runOnJS,
	useAnimatedStyle,
	useReducedMotion,
	useSharedValue,
	withDelay,
	withTiming,
} from 'react-native-reanimated'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

/** How long the moment lasts, all in. Short enough that nobody waits for it. */
const HOLD_MS = 900
const FADE_MS = 420

/** The wordmark's width here and on the native splash. */
const SPLASH_LOGO_WIDTH = 160
/** The login's masthead: the mark runs edge to edge inside the page inset, 16pt under the safe area. */
const WORDMARK_RATIO = 488 / 1562
const PAGE_INSET = 20
const MASTHEAD_TOP = 16
const MASTHEAD_MAX_WIDTH = 600

/**
 * The launch, continued. The native splash is the wordmark on the pink; this picks up exactly
 * where it left off (same mark, same place, same ground), holds a beat, and then hands over to
 * the app.
 *
 * Two hand-offs. Signed in, the cover simply lifts to reveal Home. Signed out, the mark travels
 * up and grows into the login's edge-to-edge masthead while the cover fades, so the splash
 * becomes the front door in one motion rather than being replaced by it.
 *
 * Under Reduce Motion the cover leaves without moving.
 */
export const BrandSplash = ({ onDone }: { onDone: () => void }) => {
	const reduceMotion = useReducedMotion()
	const { isAuthenticated } = useAuthStore()
	const insets = useSafeAreaInsets()
	const { width, height } = useWindowDimensions()
	const cover = useSharedValue(1)

	// Where the login masthead's centre is, relative to this mark's centre on the screen.
	const travelsToHeader = !isAuthenticated && !reduceMotion
	const mastheadWidth = Math.min(width, MASTHEAD_MAX_WIDTH) - PAGE_INSET * 2
	const mastheadCenterY = insets.top + MASTHEAD_TOP + (mastheadWidth * WORDMARK_RATIO) / 2
	const targetDy = mastheadCenterY - height / 2
	const targetScale = mastheadWidth / SPLASH_LOGO_WIDTH

	useEffect(() => {
		cover.value = withDelay(
			reduceMotion ? 500 : HOLD_MS,
			withTiming(
				0,
				{ duration: reduceMotion ? 0 : FADE_MS, easing: Easing.inOut(Easing.cubic) },
				(finished) => {
					if (finished) runOnJS(onDone)()
				}
			)
		)
	}, [reduceMotion, cover, onDone])

	const groundStyle = useAnimatedStyle(() => ({ opacity: cover.value }))

	// The mark outlives the ground by a beat, so it is still visible while it travels.
	const markStyle = useAnimatedStyle(() => {
		const progress = 1 - cover.value
		if (!travelsToHeader) {
			return {
				opacity: cover.value,
				transform: [{ scale: 1 + progress * 0.04 }],
			}
		}
		return {
			opacity: interpolate(progress, [0, 0.85, 1], [1, 1, 0]),
			transform: [
				{ translateY: interpolate(progress, [0, 1], [0, targetDy]) },
				{ scale: interpolate(progress, [0, 1], [1, targetScale]) },
			],
		}
	})

	// On the photograph the masthead is white, so the travelling mark turns white on the way.
	const whiteMarkStyle = useAnimatedStyle(() => ({
		opacity: travelsToHeader ? 1 - cover.value : 0,
	}))

	return (
		<View
			pointerEvents="auto"
			style={StyleSheet.absoluteFill}
			accessibilityElementsHidden
			importantForAccessibility="no-hide-descendants"
		>
			<Animated.View
				className="bg-ground"
				style={[StyleSheet.absoluteFill, groundStyle]}
			/>
			<View style={StyleSheet.absoluteFill} className="items-center justify-center">
				{/* The same width as the native splash image, so the hand-off is invisible. */}
				<Animated.View style={markStyle}>
					<Logo className="w-40 h-[50px]" />
					<Animated.View style={[StyleSheet.absoluteFill, whiteMarkStyle]}>
						<Logo className="w-40 h-[50px]" color={LogoColorVariant.LIGHT} />
					</Animated.View>
				</Animated.View>
			</View>
		</View>
	)
}
